import React from 'react';
import {type IconDefinition} from '@netx/core-icons';
import {
	Typography,
	Snackbar as MuiSnackbar,
	type SnackbarProps as MuiSnackbarProps,
	Slide,
	type SlideProps,
} from '@mui/material';
import {
	Alert,
	alertClasses,
	type AlertProps,
} from '@netx/core-components/Alert';
import {Button} from '@netx/core-components/Button';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {styled} from '@netx/core-components/Theme/styled';

type _MuiSnackbarProps = Required<MuiSnackbarProps>;

// handle snackbar transition into view
const TransitionUp = React.forwardRef<HTMLElement, SlideProps>((props, ref) => {
	return <Slide direction="up" ref={ref} {...props} />;
}) as (props: SlideProps) => JSX.Element;

const SnackbarRoot = styled(MuiSnackbar)(({theme}) => ({
	[`& .${alertClasses.message}`]: {
		display: 'flex',
		alignItems: 'center',
	},
}));

export interface SnackbarProps
	extends MuiSnackbarProps, Pick<AlertProps, 'severity' | 'variant'> {
	preventCloseOnClickAway?: boolean;
	actionLabel?: string;
	actionIcon?: IconDefinition;
	actionCallback?: () => void;
}

export const SnackbarPrimitive = ({
	actionIcon,
	actionLabel,
	actionCallback,
	message,
	preventCloseOnClickAway,
	severity = 'info',
	variant = 'filled',
	onClose,
	...props
}: SnackbarProps) => {
	const handleClose = useConstCallback<_MuiSnackbarProps['onClose']>(
		(event, reason) => {
			if (!preventCloseOnClickAway || reason !== 'clickaway') {
				onClose?.(event, reason);
			}
		},
	);

	return (
		<SnackbarRoot
			anchorOrigin={{
				horizontal: 'center',
				vertical: 'bottom',
			}}
			autoHideDuration={6000}
			TransitionComponent={TransitionUp}
			{...props}
			onClose={handleClose}
			sx={{
				cursor: props.onClick ? 'pointer' : undefined,
				...props.sx,
			}}
		>
			<Alert
				onClose={(event) => {
					event.stopPropagation();
					handleClose(event, 'escapeKeyDown');
				}}
				severity={severity}
				variant={variant}
			>
				<Typography sx={{flex: '1 1 auto'}}>{message}</Typography>
				{actionCallback && actionLabel ? (
					<Button
						variant="outlined"
						size="small"
						color="inherit"
						sx={{flex: '0 0 auto', ml: 2}}
						startIcon={actionIcon}
						onClick={(event) => {
							actionCallback();
							handleClose(event, 'escapeKeyDown');
						}}
					>
						{actionLabel}
					</Button>
				) : null}
			</Alert>
		</SnackbarRoot>
	);
};

export const Snackbar = SnackbarPrimitive;

export default Snackbar;
